import { defineStore } from 'pinia';
import {
  listsApi,
  itemsApi,
  type CreateListBody,
  type UpdateListBody,
} from '@/services/api/endpoints';
import { ApiError, NetworkError } from '@/services/api/client';
import { localDb, type OutboxOp } from '@/services/db/localDb';
import {
  cloneTree,
  findNode,
  insertAfter,
  makeLeaf,
  recompute,
  removeNode,
  setSubtreeDone,
} from '@/services/tasks/tree';
import { uid, tempId, isTempId } from '@/shared/lib/uid';
import { urgencyWeight } from '@/shared/lib/urgency';
import { kv } from '@/services/platform';
import { useAuthStore } from './auth';
import { useSyncStore } from './sync';
import { useUiStore } from './ui';
import type { ItemNode, ListDetail, ListSummary, Urgency } from '@/types/domain';

export type Area = 'all' | 'mine' | 'family';
export type Grouping = 'list' | 'urgency' | 'people';

export interface SavedView {
  id: string;
  name: string;
  area: Area;
  urgentOnly: boolean;
  adultsOnly: boolean;
  grouping: Grouping;
}

interface ViewPrefs {
  area: Area;
  urgentOnly: boolean;
  adultsOnly: boolean;
  grouping: Grouping;
  hideDone: boolean;
  savedViews: SavedView[];
}

const PREFS_KEY = 'td.tasks.viewPrefs';

// разделяемый промис дренажа — повторные вызовы ждут текущий, а не стартуют параллельный
let drainInFlight: Promise<void> | null = null;

interface State {
  summaries: ListSummary[];
  detailCache: Record<string, ListDetail>;
  openListId: string | null;
  loadingIndex: boolean;
  loadingDetail: boolean;
  draining: boolean;
  prefs: ViewPrefs;
}

const DEFAULT_PREFS: ViewPrefs = {
  area: 'all',
  urgentOnly: false,
  adultsOnly: false,
  grouping: 'list',
  hideDone: false,
  savedViews: [],
};

export const useTasksStore = defineStore('tasks', {
  state: (): State => ({
    summaries: [],
    detailCache: {},
    openListId: null,
    loadingIndex: false,
    loadingDetail: false,
    draining: false,
    prefs: { ...DEFAULT_PREFS },
  }),

  getters: {
    openDetail(s): ListDetail | null {
      return s.openListId ? (s.detailCache[s.openListId] ?? null) : null;
    },

    /** Списки после area+чипов, сгруппированные/отсортированные для индекса. */
    visibleLists(s): { pinned: ListSummary[]; groups: { key: string; title: string; lists: ListSummary[] }[] } {
      const auth = useAuthStore();
      const meId = auth.user?.id;
      let lists = s.summaries.filter((l) => !l.archived);

      if (s.prefs.area === 'mine') {
        lists = lists.filter((l) => l.ownerId === meId || l.assigneeIds.includes(meId ?? ''));
      } else if (s.prefs.area === 'family') {
        // семейные = общие списки + собственные «Входящие» (чужие инбоксы скрыты)
        lists = lists.filter((l) => !l.isInbox || l.ownerId === meId);
      }
      if (s.prefs.urgentOnly) lists = lists.filter((l) => l.urgency === 'urgent');
      if (s.prefs.adultsOnly) lists = lists.filter((l) => l.adultsOnly);

      const pinned = lists.filter((l) => l.pinned);
      const rest = lists.filter((l) => !l.pinned);

      if (s.prefs.grouping === 'urgency') {
        const order: Urgency[] = ['urgent', 'high', 'medium', 'low', 'none'];
        const titles: Record<Urgency, string> = {
          urgent: 'Горящие',
          high: 'Высокие',
          medium: 'Средние',
          low: 'Низкие',
          none: 'Без срочности',
        };
        const groups = order
          .map((u) => ({ key: u, title: titles[u], lists: rest.filter((l) => l.urgency === u) }))
          .filter((g) => g.lists.length);
        return { pinned, groups };
      }
      if (s.prefs.grouping === 'people') {
        const byOwner = new Map<string, ListSummary[]>();
        for (const l of rest) {
          const arr = byOwner.get(l.ownerId) ?? [];
          arr.push(l);
          byOwner.set(l.ownerId, arr);
        }
        const groups = [...byOwner.entries()].map(([ownerId, ls]) => ({
          key: ownerId,
          title: ownerId === meId ? 'Мои' : 'Семья',
          lists: ls,
        }));
        return { pinned, groups };
      }
      return { pinned, groups: [{ key: 'all', title: '', lists: rest }] };
    },
  },

  actions: {
    // ---------- инициализация ----------
    async bootstrap() {
      const prefs = await kv.getJson<ViewPrefs>(PREFS_KEY);
      if (prefs) this.prefs = { ...DEFAULT_PREFS, ...prefs };
      this.summaries = await localDb.getSummaries();
      await this.refreshPendingCount();
      void this.loadIndex();
      void this.drainOutbox();
    },

    async savePrefs() {
      await kv.setJson(PREFS_KEY, this.prefs);
    },

    async refreshPendingCount() {
      const ob = await localDb.getOutbox();
      useSyncStore().setPending(ob.length);
    },

    // ---------- загрузка с сервера ----------
    async loadIndex() {
      const sync = useSyncStore();
      if (!sync.online) return;
      this.loadingIndex = true;
      try {
        const lists = await listsApi.list(false);
        this.summaries = lists;
        await localDb.setSummaries(lists);
        sync.markSynced();
      } catch (e) {
        if (!(e instanceof NetworkError)) console.error('loadIndex', e);
      } finally {
        this.loadingIndex = false;
      }
    },

    async loadDetail(id: string) {
      this.openListId = id;
      const cached = this.detailCache[id] ?? (await localDb.getDetail(id));
      if (cached) this.detailCache[id] = cached;
      // temp-id (ещё не синканный список) — на сервере его нет, GET вернёт 400. Только кэш.
      if (isTempId(id)) return;
      const sync = useSyncStore();
      if (!sync.online) return;
      this.loadingDetail = true;
      try {
        const detail = await listsApi.get(id);
        this.detailCache[id] = detail;
        await localDb.setDetail(detail);
      } catch (e) {
        if (!(e instanceof NetworkError)) console.error('loadDetail', e);
      } finally {
        this.loadingDetail = false;
      }
    },

    // ---------- outbox ----------
    async enqueue(op: Omit<OutboxOp, 'id' | 'createdAt' | 'retries'>) {
      await localDb.appendOutboxOp({ ...op, id: uid(), createdAt: Date.now(), retries: 0 });
      await this.refreshPendingCount();
      void this.drainOutbox();
    },

    /** Дренаж очереди при онлайне. Повторные вызовы ждут текущий промис (без параллельного дренажа).
     *  В хвосте перепроверяет очередь — добивает ops, добавленные у самой границы цикла (MAJOR-1). */
    drainOutbox(): Promise<void> {
      const sync = useSyncStore();
      if (!sync.online) return Promise.resolve();
      if (drainInFlight) return drainInFlight;
      drainInFlight = this.runDrain()
        .finally(() => {
          drainInFlight = null;
        })
        .then(async () => {
          const rest = await localDb.getOutbox();
          if (rest.length && useSyncStore().online) await this.drainOutbox();
        });
      return drainInFlight;
    },

    async runDrain() {
      const sync = useSyncStore();
      const initial = await localDb.getOutbox();
      if (!initial.length) return;
      this.draining = true;
      sync.setSyncing(true);
      try {
        // голову очереди перечитываем каждую итерацию — ops, ремапнутые/добавленные во время
        // дренажа, уже несут реальные id (remapOutboxIds персистит temp→real).
        for (;;) {
          const queue = await localDb.getOutbox();
          if (!queue.length) break;
          const op = queue[0];
          try {
            await this.sendOp(op);
            await localDb.removeOutboxOp(op.id);
          } catch (e) {
            if (e instanceof NetworkError) break; // оффлайн — op остаётся в очереди
            // постоянная ошибка (4xx) — снимаем op, чтобы не блокировать очередь
            console.error('outbox op failed', op.kind, e);
            if (e instanceof ApiError) {
              if (e.status === 403) useUiStore().toast('Недостаточно прав для операции', 'error');
              else if (e.status === 409)
                useUiStore().toast('Изменено другим участником — обновлено', 'info');
            }
            await localDb.removeOutboxOp(op.id);
          }
          await this.refreshPendingCount();
        }
      } finally {
        this.draining = false;
        sync.setSyncing(false);
      }
      // реконсиляция истины с сервера
      await this.loadIndex();
      if (this.openListId && !isTempId(this.openListId)) await this.loadDetail(this.openListId);
    },

    /** Применить temp→real id к in-memory состоянию (после ACK create). */
    applyRemap(fromId: string, toId: string) {
      if (this.openListId === fromId) this.openListId = toId;
      if (this.detailCache[fromId]) {
        const d = this.detailCache[fromId];
        this.detailCache[toId] = { ...d, id: toId };
        delete this.detailCache[fromId];
      }
      const s = this.summaries.find((x) => x.id === fromId);
      if (s) s.id = toId;
    },

    async sendOp(op: OutboxOp) {
      const p = op.payload;
      switch (op.kind) {
        case 'list.create': {
          const res = await listsApi.create(p as unknown as CreateListBody);
          await localDb.remapOutboxIds(op.localId, res.id);
          this.applyRemap(op.localId, res.id);
          break;
        }
        case 'list.update':
          await listsApi.update(op.localId, p as UpdateListBody);
          break;
        case 'list.delete':
          await listsApi.remove(op.localId);
          break;
        case 'list.checkAll':
          await listsApi.checkAll(op.localId, p.done as boolean);
          break;
        case 'item.create': {
          const res = await itemsApi.create(op.listLocalId!, {
            text: p.text as string,
            urgency: p.urgency as Urgency | undefined,
            parentId: p.parentId as string | undefined,
            afterItemId: p.afterItemId as string | undefined,
            clientId: p.clientId as string | undefined,
          });
          await localDb.remapOutboxIds(op.localId, res.id);
          break;
        }
        case 'item.update':
          await itemsApi.update(op.localId, {
            text: p.text as string | undefined,
            urgency: p.urgency as Urgency | undefined,
            baseVersion: p.baseVersion as number | undefined,
          });
          break;
        case 'item.delete':
          await itemsApi.remove(op.localId);
          break;
        case 'item.check':
          await itemsApi.check(op.localId, p.done as boolean);
          break;
        case 'item.move':
          await itemsApi.move(op.localId, {
            parentId: (p.parentId as string | undefined) ?? null,
            afterItemId: p.afterItemId as string | undefined,
          });
          break;
      }
    },

    // ---------- утилиты локального дерева ----------
    patchDetailTree(listId: string, mutate: (items: ItemNode[]) => void) {
      const d = this.detailCache[listId];
      if (!d) return;
      const items = cloneTree(d.items);
      mutate(items);
      recompute(items);
      this.detailCache[listId] = { ...d, items };
      void localDb.setDetail(this.detailCache[listId]);
      this.recomputeSummaryRollup(listId);
    },

    /** Пересчёт rollup/itemCount карточки в индексе из дерева детали. */
    recomputeSummaryRollup(listId: string) {
      const d = this.detailCache[listId];
      const s = this.summaries.find((x) => x.id === listId);
      if (!d || !s) return;
      const done = d.items.reduce((acc, n) => acc + n.rollup.doneLeaves, 0);
      const total = d.items.reduce((acc, n) => acc + n.rollup.totalLeaves, 0);
      let count = 0;
      const walk = (ns: ItemNode[]) => ns.forEach((n) => (count++, walk(n.children)));
      walk(d.items);
      s.rollup = { doneLeaves: done, totalLeaves: total };
      s.itemCount = count;
      void localDb.setSummaries(this.summaries);
    },

    // ---------- мутации списков ----------
    async createList(body: CreateListBody): Promise<string> {
      const auth = useAuthStore();
      const localId = tempId();
      const clientId = uid();
      const now = new Date().toISOString();
      const summary: ListSummary = {
        id: localId,
        householdId: auth.user?.householdId ?? '',
        ownerId: auth.user?.id ?? '',
        title: body.title,
        urgency: body.urgency ?? 'none',
        adultsOnly: body.adultsOnly ?? false,
        isInbox: false,
        markMode: body.markMode ?? 'shared',
        pinned: false,
        archived: false,
        rollupDirectOnly: false,
        position: (this.summaries.at(-1)?.position ?? 0) + 1000,
        version: 0,
        clientId,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        assigneeIds: [],
        rollup: { doneLeaves: 0, totalLeaves: 0 },
        itemCount: 0,
      };
      this.summaries.push(summary);
      this.detailCache[localId] = { ...summary, items: [] } as unknown as ListDetail;
      await localDb.setSummaries(this.summaries);
      await this.enqueue({ kind: 'list.create', localId, payload: { ...body, clientId } });
      // дожидаемся синка, чтобы навигация шла на реальный id (иначе GET /lists/temp_ → 400)
      await this.drainOutbox();
      const real = this.summaries.find((l) => l.clientId === clientId);
      return real?.id ?? localId;
    },

    async updateList(id: string, patch: UpdateListBody) {
      const s = this.summaries.find((x) => x.id === id);
      if (s) {
        Object.assign(s, patch);
        await localDb.setSummaries(this.summaries);
      }
      const d = this.detailCache[id];
      if (d) {
        Object.assign(d, patch);
        await localDb.setDetail(d);
      }
      await this.enqueue({ kind: 'list.update', localId: id, payload: patch as Record<string, unknown> });
    },

    async pinList(id: string) {
      const s = this.summaries.find((x) => x.id === id);
      await this.updateList(id, { pinned: !s?.pinned });
    },

    async archiveList(id: string) {
      await this.updateList(id, { archived: true });
      this.summaries = this.summaries.filter((x) => x.id !== id);
      await localDb.setSummaries(this.summaries);
    },

    async deleteList(id: string) {
      this.summaries = this.summaries.filter((x) => x.id !== id);
      delete this.detailCache[id];
      await localDb.setSummaries(this.summaries);
      await localDb.removeDetail(id);
      await this.enqueue({ kind: 'list.delete', localId: id, payload: {} });
    },

    async checkAllList(id: string, done: boolean) {
      this.patchDetailTree(id, (items) => {
        const all = (ns: ItemNode[]) => ns.forEach((n) => ((n.done = done), all(n.children)));
        all(items);
      });
      await this.enqueue({ kind: 'list.checkAll', localId: id, payload: { done } });
    },

    // ---------- мутации пунктов ----------
    async addItem(
      listId: string,
      opts: { text: string; parentId?: string | null; afterItemId?: string | null; urgency?: Urgency },
    ): Promise<string> {
      const localId = tempId();
      const clientId = uid();
      const parentId = opts.parentId ?? null;
      // guard глубины ≤4 уровней (depth 0..3): нельзя добавить ребёнка к узлу depth=3 (MAJOR-4)
      if (parentId) {
        const d = this.detailCache[listId];
        const parent = d ? findNode(d.items, parentId) : null;
        if (parent && parent.depth >= 3) {
          useUiStore().toast('Достигнута максимальная вложенность (4 уровня)', 'info');
          return '';
        }
      }
      this.patchDetailTree(listId, (items) => {
        const parent = parentId ? findNode(items, parentId) : null;
        const depth = parent ? parent.depth + 1 : 0;
        const node = makeLeaf(localId, opts.text, opts.urgency ?? 'none', depth);
        node.parentId = parentId;
        const siblings = parent ? parent.children : items;
        insertAfter(siblings, node, opts.afterItemId ?? null);
      });
      await this.enqueue({
        kind: 'item.create',
        localId,
        listLocalId: listId,
        payload: {
          text: opts.text,
          urgency: opts.urgency,
          parentId: parentId ?? undefined,
          afterItemId: opts.afterItemId ?? undefined,
          clientId,
        },
      });
      return localId;
    },

    async updateItem(listId: string, itemId: string, patch: { text?: string; urgency?: Urgency }) {
      let baseVersion: number | undefined;
      this.patchDetailTree(listId, (items) => {
        const node = findNode(items, itemId);
        if (!node) return;
        baseVersion = node.version;
        if (patch.text !== undefined) node.text = patch.text;
        if (patch.urgency !== undefined) node.urgency = patch.urgency;
      });
      const payload: Record<string, unknown> = { ...patch };
      if (patch.text !== undefined && !isTempId(itemId)) payload.baseVersion = baseVersion;
      await this.enqueue({ kind: 'item.update', localId: itemId, payload });
    },

    async checkItem(listId: string, itemId: string, done: boolean) {
      this.patchDetailTree(listId, (items) => {
        const node = findNode(items, itemId);
        if (node) setSubtreeDone(node, done); // каскад как на сервере
      });
      await this.enqueue({ kind: 'item.check', localId: itemId, payload: { done } });
    },

    async deleteItem(listId: string, itemId: string) {
      this.patchDetailTree(listId, (items) => {
        removeNode(items, itemId);
      });
      await this.enqueue({ kind: 'item.delete', localId: itemId, payload: {} });
    },

    /** Перемещение/смена уровня (drag-reorder, B9). */
    async moveItem(
      listId: string,
      itemId: string,
      target: { parentId: string | null; afterItemId: string | null },
    ) {
      // нельзя перемещать узел внутрь самого себя / своего поддерева (MAJOR-5)
      if (target.parentId) {
        const d = this.detailCache[listId];
        const moving = d ? findNode(d.items, itemId) : null;
        if (moving && findNode([moving], target.parentId)) return;
      }
      this.patchDetailTree(listId, (items) => {
        const node = removeNode(items, itemId);
        if (!node) return;
        const parent = target.parentId ? findNode(items, target.parentId) : null;
        const newDepth = parent ? parent.depth + 1 : 0;
        const delta = newDepth - node.depth;
        node.parentId = target.parentId;
        const applyDepth = (n: ItemNode, d: number) => {
          n.depth += d;
          n.children.forEach((c) => applyDepth(c, d));
        };
        applyDepth(node, delta);
        const siblings = parent ? parent.children : items;
        insertAfter(siblings, node, target.afterItemId);
      });
      await this.enqueue({
        kind: 'item.move',
        localId: itemId,
        payload: {
          parentId: target.parentId ?? undefined,
          afterItemId: target.afterItemId ?? undefined,
        },
      });
    },

    // ---------- ответственные / персональные отметки (P2; online-first optimistic, НЕ через outbox) ----------
    // ВНИМАНИЕ: эти мутации не попадают в offline-outbox (как lists/items). При NetworkError —
    // откатываем локально (иначе UI «соврёт», что сохранил). Перевод в outbox — задел P2.x.
    async setListAssignees(listId: string, userIds: string[]) {
      const s = this.summaries.find((x) => x.id === listId);
      const d = this.detailCache[listId];
      const prior = [...(s?.assigneeIds ?? d?.assigneeIds ?? [])];
      if (s) s.assigneeIds = [...userIds];
      if (d) d.assigneeIds = [...userIds];
      try {
        await listsApi.setAssignees(listId, userIds);
      } catch (e) {
        if (e instanceof NetworkError) {
          if (s) s.assigneeIds = prior;
          if (d) d.assigneeIds = prior;
          useUiStore().toast('Нет соединения — не сохранено', 'error');
        } else {
          useUiStore().toast('Не удалось сохранить ответственных', 'error');
          console.error(e);
          void this.loadDetail(listId);
        }
      }
    },

    async setItemAssignees(listId: string, itemId: string, userIds: string[]) {
      const prior = [...(findNode(this.detailCache[listId]?.items ?? [], itemId)?.assigneeIds ?? [])];
      this.patchDetailTree(listId, (items) => {
        const n = findNode(items, itemId);
        if (n) n.assigneeIds = [...userIds];
      });
      try {
        await itemsApi.setAssignees(itemId, userIds);
      } catch (e) {
        if (e instanceof NetworkError) {
          this.patchDetailTree(listId, (items) => {
            const n = findNode(items, itemId);
            if (n) n.assigneeIds = prior;
          });
          useUiStore().toast('Нет соединения — не сохранено', 'error');
        } else {
          useUiStore().toast('Не удалось сохранить ответственных', 'error');
          console.error(e);
          void this.loadDetail(listId);
        }
      }
    },

    /** Персональная отметка текущего пользователя (markMode=personal). */
    async togglePersonalCheck(listId: string, itemId: string, userId: string, checked: boolean) {
      const prior = [...(findNode(this.detailCache[listId]?.items ?? [], itemId)?.checkedBy ?? [])];
      this.patchDetailTree(listId, (items) => {
        const n = findNode(items, itemId);
        if (!n) return;
        n.checkedBy = checked
          ? [...new Set([...n.checkedBy, userId])]
          : n.checkedBy.filter((x) => x !== userId);
      });
      try {
        await itemsApi.checkPersonal(itemId, checked);
      } catch (e) {
        if (e instanceof NetworkError) {
          this.patchDetailTree(listId, (items) => {
            const n = findNode(items, itemId);
            if (n) n.checkedBy = prior;
          });
          useUiStore().toast('Нет соединения — не сохранено', 'error');
        } else {
          useUiStore().toast('Не удалось отметить', 'error');
          console.error(e);
          void this.loadDetail(listId);
        }
      }
    },

    // ---------- сохранённые виды (B5) ----------
    async saveCurrentView(name: string) {
      this.prefs.savedViews.push({
        id: uid(),
        name,
        area: this.prefs.area,
        urgentOnly: this.prefs.urgentOnly,
        adultsOnly: this.prefs.adultsOnly,
        grouping: this.prefs.grouping,
      });
      await this.savePrefs();
    },
    async applyView(v: SavedView) {
      this.prefs.area = v.area;
      this.prefs.urgentOnly = v.urgentOnly;
      this.prefs.adultsOnly = v.adultsOnly;
      this.prefs.grouping = v.grouping;
      await this.savePrefs();
    },
    async deleteView(id: string) {
      this.prefs.savedViews = this.prefs.savedViews.filter((v) => v.id !== id);
      await this.savePrefs();
    },

    reset() {
      this.summaries = [];
      this.detailCache = {};
      this.openListId = null;
    },
  },
});

/** Видимое дерево с учётом hideDone + сортировки (используется во вью детали). */
export function visibleTree(items: ItemNode[], hideDone: boolean, sortByUrgency = false): ItemNode[] {
  let nodes = items;
  if (hideDone) nodes = nodes.filter((n) => !(n.done && n.children.length === 0));
  const mapped = nodes.map((n) => ({ ...n, children: visibleTree(n.children, hideDone, sortByUrgency) }));
  if (sortByUrgency) {
    mapped.sort((a, b) => urgencyWeight(a.urgency) - urgencyWeight(b.urgency) || a.position - b.position);
  }
  return mapped;
}
